import { FormattedMessage } from 'react-intl';
import Avatar from '../design-system/Avatar';
import { User } from '@phosphor-icons/react';
import { Msg } from '../i18n-review/client/Msg';

/** Europass-style CV content used in the profile preview modal, the tailor-CV modal, and PDF export. */
export function EuropassCVContent({
  userName,
  userPhoto,
  profileTitle,
  mostRecentWorkExp,
  workExperiencesByDate,
  educationByDate,
  skills,
  projectsByYear,
  formatDateRange,
}: {
  userName: string | null;
  userPhoto: string | null;
  profileTitle: string;
  mostRecentWorkExp: { title: string; company: string } | null;
  workExperiencesByDate: Array<{ id: string; title: string; company: string; startDate: string; endDate: string; description?: string }>;
  educationByDate: Array<{ id: string; degree: string; institution: string; startDate: string; endDate: string }>;
  skills: string[];
  projectsByYear: Array<{ id: string; title: string; year: string; description: string }>;
  formatDateRange: (start: string, end: string) => string;
}) {
  return (
    <div
      className="pdf-export-cv mx-auto bg-white dark:bg-[#1F1509] rounded-lg shadow-lg p-8 max-w-[210mm] min-h-[297mm] text-[#1E1A14] dark:text-[#F2EADF]"
      style={{ background: '#ffffff', color: '#1E1A14' }}
    >
      <div className="flex items-start justify-between gap-6 pb-4 border-b-2 border-[#3B5E45]">
        <div className="flex-1 min-w-0">
          {userName && <h1 className="text-2xl font-bold mb-1">{userName}</h1>}
          {mostRecentWorkExp ? (
            <p className="text-base text-[#3D3529]">
              {mostRecentWorkExp.title}
              {mostRecentWorkExp.company ? ` – ${mostRecentWorkExp.company}` : ''}
            </p>
          ) : profileTitle ? (
            <p className="text-base text-[#3D3529]">{profileTitle}</p>
          ) : null}
        </div>
        <div className="shrink-0">
          {userPhoto ? (
            <Avatar src={userPhoto} size={80} name={userName || 'User'} />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#DDD5C8] flex items-center justify-center">
              <User className="w-10 h-10 text-[#7A6E5F]" />
            </div>
          )}
        </div>
      </div>

      <div className="pt-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-[#3B5E45] mb-2 border-b border-[#DDD5C8] pb-1">
          <Msg id="profile.europass.workExperience" defaultMessage="Work experience" description="Europass CV section: work experience" />
        </h2>
        {workExperiencesByDate.length === 0 ? (
          <p className="text-sm text-[#7A6E5F]">
            <Msg id="profile.europass.noWorkExperience" defaultMessage="No work experience added." description="Empty state for work experience" />
          </p>
        ) : (
          <ul className="space-y-3 list-none pl-0">
            {workExperiencesByDate.map((exp) => (
              <li key={exp.id} className="border-l-2 border-[#DDD5C8] pl-3">
                <p className="font-semibold">
                  {exp.title || '—'}
                  {exp.company ? ` · ${exp.company}` : ''}
                </p>
                <p className="text-xs text-[#7A6E5F]">{formatDateRange(exp.startDate, exp.endDate)}</p>
                {exp.description && <p className="text-sm text-[#3D3529] mt-1">{exp.description}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pt-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-[#3B5E45] mb-2 border-b border-[#DDD5C8] pb-1">
          <Msg id="profile.europass.educationAndTraining" defaultMessage="Education and training" description="Europass CV section: education and training" />
        </h2>
        {educationByDate.length === 0 ? (
          <p className="text-sm text-[#7A6E5F]">
            <Msg id="profile.europass.noEducation" defaultMessage="No education added." description="Empty state for education" />
          </p>
        ) : (
          <ul className="space-y-3 list-none pl-0">
            {educationByDate.map((edu) => (
              <li key={edu.id} className="border-l-2 border-[#DDD5C8] pl-3">
                <p className="font-semibold">
                  {edu.degree || '—'}
                  {edu.institution ? ` · ${edu.institution}` : ''}
                </p>
                <p className="text-xs text-[#7A6E5F]">{formatDateRange(edu.startDate, edu.endDate)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pt-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-[#3B5E45] mb-2 border-b border-[#DDD5C8] pb-1">
          <Msg id="profile.europass.personalSkills" defaultMessage="Personal skills" description="Europass CV section: personal skills" />
        </h2>
        {skills.length === 0 ? (
          <p className="text-sm text-[#7A6E5F]">
            <Msg id="profile.europass.noSkills" defaultMessage="No skills added." description="Empty state for skills" />
          </p>
        ) : (
          <p className="text-sm text-[#3D3529]">{skills.filter(Boolean).join(' · ') || '—'}</p>
        )}
      </div>

      {projectsByYear.length > 0 && (
        <div className="pt-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[#3B5E45] mb-2 border-b border-[#DDD5C8] pb-1">
            <Msg id="profile.europass.additionalInfo" defaultMessage="Additional information" description="Europass CV section: additional information" />
          </h2>
          <ul className="space-y-3 list-none pl-0">
            {projectsByYear.map((proj) => (
              <li key={proj.id} className="border-l-2 border-[#DDD5C8] pl-3">
                <p className="font-semibold">
                  {proj.title || '—'}
                  {proj.year && ` (${proj.year})`}
                </p>
                {proj.description && <p className="text-sm text-[#3D3529] mt-1">{proj.description}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-6 pt-4 border-t border-[#DDD5C8] text-xs text-[#7A6E5F]">
        <Msg id="profile.europass.basedOn" defaultMessage="Based on Europass CV structure (europass.europa.eu)" description="Footnote that CV follows Europass standard" />
      </p>
    </div>
  );
}
